import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "src/components/ui/Dialog";
import { Badge } from "src/components/ui/Badge";
import { Button } from "src/components/ui/Button";
import { Card } from "src/components/ui/Card";
import { Star, Clock, MapPin, BookOpen, Award, Calendar, Briefcase, GraduationCap, Languages, Users, ThumbsUp, MessageCircle } from "lucide-react";
import { Link } from "react-router-dom";

// Clean Featured Review Component
const FeaturedReview = ({ review }: { review: any }) => (
  <div className="p-6 bg-white/60 backdrop-blur-sm border border-slate-200/50 rounded-lg hover:bg-white/70 transition-all duration-300">
    <div className="flex items-center gap-3 mb-4">
      <div className="flex items-center gap-1">
        {[...Array(5)].map((_, i) => (
          <Star 
            key={i} 
            size={14} 
            className={i < 5 ? "text-slate-400 fill-slate-400" : "text-slate-200"} 
          />
        ))}
      </div>
      <div className="w-px h-4 bg-slate-200"></div>
      <span className="font-medium text-slate-700">{review.studentName || review.student?.name || 'Anonymous'}</span>
      <span className="text-xs text-slate-500">{review.date || review.createdAt || ''}</span>
    </div>
    
    <p className="text-slate-600 leading-relaxed mb-4">
      "{review.comment || review.text || review.review || ''}"
    </p>
    
    {review.tutorResponse && (
      <div className="pl-4 border-l-2 border-blue-200 bg-blue-50/30 p-3 rounded-r-lg">
        <span className="text-sm font-medium text-blue-700">Tutor Response:</span>
        <p className="text-sm text-slate-600 mt-1">{review.tutorResponse}</p>
      </div>
    )}
  </div>
);

// Clean section header
const SectionHeader = ({ icon: Icon, title }: { icon: any, title: string }) => (
  <div className="flex items-center gap-3 mb-4">
    <Icon className="text-slate-500" size={20} />
    <h3 className="font-semibold text-slate-800">{title}</h3>
  </div>
);

interface TutorProfileModalProps {
  tutor: any;
  isOpen: boolean;
  onClose: () => void;
  showBookingButton?: boolean;
}

const TutorProfileModal = ({
  tutor,
  isOpen,
  onClose,
  showBookingButton = true,
}: TutorProfileModalProps) => {
  if (!tutor) return null;

  // Helper function to get experience years
  const getExperienceYears = () => {
    
    // Handle Search.tsx data structure (total_years_exp)
    if (tutor.total_years_exp) {
      return `${tutor.total_years_exp} years experience`;
    }
    // Handle Profile.tsx data structure (experience array)
    if (Array.isArray(tutor.experience) && tutor.experience.length > 0) {
      const totalYears = tutor.experience.reduce((sum: number, exp: any) => {
        return sum + (exp.years || 0);
      }, 0);
      return totalYears > 0 ? `${totalYears} years experience` : 'Experienced';
    }
    return 'Experienced';
  };

  // Helper function to get availability
  const getAvailability = () => {

    // Check for availability_slots (snake_case) first
    if (Array.isArray(tutor.availability_slots) && tutor.availability_slots.length > 0) {
      const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
      const availableDays = Array.from(
        new Set(tutor.availability_slots.map((slot: any) => dayNames[slot.day_of_week]))
      );
      return availableDays.map((day: string, index: number) => (
        <Badge key={index} className="bg-blue-100 text-blue-700 border-blue-200">
          {day}
        </Badge>
      ));
    }

    // Check for availabilitySlots (camelCase) as fallback
    if (Array.isArray(tutor.availabilitySlots) && tutor.availabilitySlots.length > 0) {
      const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
      const availableDays = Array.from(
        new Set(tutor.availabilitySlots.map((slot: any) => dayNames[slot.day_of_week]))
      );
      return availableDays.map((day: string, index: number) => (
        <Badge key={index} className="bg-blue-100 text-blue-700 border-blue-200">
          {day}
        </Badge>
      ));
    }

    // Default fallback when no availability slots are found
    return (
      <Badge className="bg-blue-100 text-blue-700 border-blue-200">
        Contact for availability
      </Badge>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => {
      if (!open) {
        onClose();
      }
    }}>
      <DialogContent className="max-w-4xl max-h-[95vh] overflow-hidden flex flex-col bg-white/95 backdrop-blur-sm border border-slate-200/50 shadow-xl">
        <DialogHeader className="p-6 border-b border-slate-200/50">
          <DialogTitle className="text-xl font-semibold text-slate-800">Tutor Profile</DialogTitle>
        </DialogHeader>

        <div
          className="flex-1 overflow-y-auto overflow-x-hidden p-6 focus:outline-none"
          tabIndex={0}
          style={{ overscrollBehavior: 'contain' }}
        >
          <div className="flex flex-col lg:flex-row gap-12">
            {/* Left Column - Profile Image and Key Info */}
            <div className="lg:w-1/3">
              <div className="sticky top-0">
                <div className="relative mb-6">
                  <img
                    src={tutor.profile_picture_url || tutor.photo || tutor.profilePictureUrl || "/images/tutors/default.jpg"}
                    alt={`${tutor.first_name || ''} ${tutor.last_name || ''}`.trim() || tutor.name || 'Tutor'}
                    className="w-full h-80 rounded-lg object-cover border border-slate-200/50"
                  />
                  <div className="absolute top-3 right-3 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></div>
                </div>

                {/* Basic Info */}
                <div className="space-y-4">
              {(tutor.rating && Number(tutor.rating) > 0) ? (
                    <div className="flex items-center gap-2 p-3 bg-white/50 backdrop-blur-sm rounded-lg border border-slate-200/50">
                      <Star className="text-slate-400 fill-slate-400" size={16} />
                      <span className="font-medium text-slate-700">{tutor.rating}</span>
                      {((Array.isArray(tutor.reviews) && tutor.reviews.length > 0) || (tutor.reviewCount && tutor.reviewCount > 0)) && (
                        <span className="text-sm text-slate-500">
                          ({Array.isArray(tutor.reviews) ? tutor.reviews.length : tutor.reviewCount} reviews)
                        </span>
                      )}
                    </div>
              ) : null}

              {tutor.location && (
                    <div className="flex items-center gap-2 p-3 bg-white/50 backdrop-blur-sm rounded-lg border border-slate-200/50">
                      <MapPin className="text-slate-400" size={16} />
                      <span className="text-slate-700">{tutor.location}</span>
                </div>
              )}

                  {(tutor.experience || tutor.total_years_exp) && (
                    <div className="flex items-center gap-2 p-3 bg-white/50 backdrop-blur-sm rounded-lg border border-slate-200/50">
                      <Clock className="text-slate-400" size={16} />
                      <span className="text-slate-700">
                        {getExperienceYears()}
                  </span>
                </div>
              )}
                </div>

                {/* Bio */}
                {tutor.bio && (
                  <div className="mt-6 p-4 bg-white/50 backdrop-blur-sm rounded-lg border border-slate-200/50">
                    <SectionHeader icon={BookOpen} title="About" />
                    <p className="text-slate-600 leading-relaxed">{tutor.bio}</p>
                </div>
              )}

                {/* Availability - Always show for debugging */}
                <div className="mt-6 p-4 bg-white/50 backdrop-blur-sm rounded-lg border border-slate-200/50">
                  <SectionHeader icon={Calendar} title="Available" />
                  <div className="flex flex-wrap gap-2">
                    {getAvailability()}
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Detailed Information */}
            <div className="lg:w-2/3">
              {/* Header */}
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-slate-800 mb-2">
                  {`${tutor.first_name || ''} ${tutor.last_name || ''}`.trim() || 
                   tutor.name || 
                   (tutor.firstName && tutor.lastName ? `${tutor.firstName} ${tutor.lastName}` : 'Tutor')}
                </h2>
                
                {tutor.education && (
                  <p className="text-slate-600">
                    {typeof tutor.education === 'string' ? tutor.education : 
                     (Array.isArray(tutor.education) && tutor.education.length > 0 ? 
                      (() => {
                        const firstEducation = tutor.education[0];
                        return typeof firstEducation === 'string' ? firstEducation :
                               `${firstEducation.degree || firstEducation.field || ''} from ${firstEducation.institution || ''}`.replace(' from ', ' from ');
                      })() :
                      'Qualified Educator')}
                  </p>
                )}
              </div>

              {/* Specialties */}
              <div className="mb-8">
                <SectionHeader icon={Users} title="Specialties" />
                <div className="flex flex-wrap gap-3">
                  {(() => {
                    // Get specialties from multiple possible sources
                    const specialties = tutor.subjects_taught || tutor.subjectsTaught || tutor.expertise || tutor.specialties || tutor.specializations || [];
                    
                    if (specialties.length === 0) {
                      return (
                        <Badge className="bg-gray-100 text-gray-600 border-gray-200">
                          No specialties listed
                        </Badge>
                      );
                    }
                    
                    return specialties.map((specialty: any, index: number) => (
                      <Badge 
                        key={index} 
                        className="bg-slate-100 text-slate-700 border-slate-200 px-3 py-1.5 text-sm font-medium"
                      >
                        {typeof specialty === 'string' ? specialty : 
                         (specialty.name || specialty.title || specialty.subject || 'Specialty')}
                      </Badge>
                    ));
                  })()}
                </div>
              </div>

              {/* Languages */}
              {tutor.languages && (
                <div className="mb-8">
                  <SectionHeader icon={Languages} title="Languages" />
                  <div className="flex flex-wrap gap-2">
                    {Array.isArray(tutor.languages) ? 
                     tutor.languages.map((lang: any, index: number) => (
                       <Badge key={index} className="bg-blue-100 text-blue-700 border-blue-200">
                         {typeof lang === 'string' ? lang : lang.name || lang.language}
                       </Badge>
                     )) : (
                       <Badge className="bg-blue-100 text-blue-700 border-blue-200">
                         {typeof tutor.languages === 'string' ? tutor.languages : 'Multiple languages'}
                       </Badge>
                     )}
                  </div>
                </div>
              )}

              {/* Achievements */}
              {tutor.achievements && (
                <div className="mb-8">
                  <SectionHeader icon={Award} title="Achievements" />
                  <div className="space-y-3">
                    {Array.isArray(tutor.achievements) ? 
                     tutor.achievements.map((achievement: any, index: number) => (
                       <div key={index} className="flex items-start gap-3 p-3 bg-white/50 backdrop-blur-sm rounded-lg border border-slate-200/50">
                         <ThumbsUp className="text-blue-500 mt-1" size={16} />
                         <span className="text-slate-700">
                           {typeof achievement === 'string' ? achievement : 
                            (achievement.title || achievement.name || achievement.description || 'Achievement')}
                         </span>
                       </div>
                     )) : (
                       <div className="flex items-start gap-3 p-3 bg-white/50 backdrop-blur-sm rounded-lg border border-slate-200/50">
                         <ThumbsUp className="text-blue-500 mt-1" size={16} />
                         <span className="text-slate-700">
                           {typeof tutor.achievements === 'string' ? tutor.achievements : 'Achievement available'}
                         </span>
                       </div>
                     )}
                  </div>
                </div>
              )}

              {/* Professional Experience */}
              {(tutor.professionalExperience || tutor.experience) && (
                <div className="mb-8">
                  <SectionHeader icon={Briefcase} title="Experience" />
                  <div className="bg-white/50 backdrop-blur-sm rounded-lg border border-slate-200/50">
                    {Array.isArray(tutor.professionalExperience) ? 
                     tutor.professionalExperience
                       .filter((exp: any) => {
                         // Filter out experiences with empty or invalid data
                         if (typeof exp === 'string') return exp.trim();
                         const role = (exp.role || exp.title || exp.position)?.trim();
                         const organization = (exp.organization || exp.company)?.trim();
                         // Only show if role exists and organization is not empty, "0", or other invalid values
                         return role && organization && organization !== '0' && organization !== 'null' && organization !== 'undefined';
                       })
                       .map((exp: any, index: number, filteredArray: any[]) => (
                       <div key={index}>
                         <div className="p-4">
                           {typeof exp === 'string' ? (
                             <div className="text-slate-700">{exp}</div>
                           ) : (
                             <div>
                               <div className="font-medium text-slate-800">
                                 {(() => {
                                   const role = exp.role || exp.title || exp.position;
                                   return role ? role.charAt(0).toUpperCase() + role.slice(1).toLowerCase() : '';
                                 })()}
                               </div>
                               <div className="text-blue-600 text-sm">
                                 {exp.organization || exp.company || 'Organization'}
                               </div>
                               <div className="text-xs text-slate-500 mt-1">
                                 {exp.location ? `${exp.location} • ` : ''}
                                 {(exp.start_year || exp.startYear) ? `${exp.start_year || exp.startYear} - ${exp.end_year || exp.endYear || 'Present'}` : ''}
                                 {(exp.years && exp.years > 0) ? ` • ${exp.years} years` : ''}
                                 {exp.duration ? ` • ${exp.duration}` : ''}
                               </div>
                             </div>
                           )}
                         </div>
                         {index < filteredArray.length - 1 && (
                           <hr className="border-slate-200" />
                         )}
                       </div>
                     )) : Array.isArray(tutor.experience) ? 
                     tutor.experience
                       .filter((exp: any) => {
                         // Filter out experiences with empty or invalid data
                         if (typeof exp === 'string') return exp.trim();
                         const role = (exp.role || exp.title || exp.position)?.trim();
                         const organization = (exp.organization || exp.company)?.trim();
                         // Only show if role exists and organization is not empty, "0", or other invalid values
                         return role && organization && organization !== '0' && organization !== 'null' && organization !== 'undefined';
                       })
                       .map((exp: any, index: number, filteredArray: any[]) => (
                       <div key={index}>
                         <div className="p-4">
                           {typeof exp === 'string' ? (
                             <div className="text-slate-700">{exp}</div>
                           ) : (
                             <div>
                               <div className="font-medium text-slate-800">
                                 {(() => {
                                   const role = exp.role || exp.title || exp.position;
                                   return role ? role.charAt(0).toUpperCase() + role.slice(1).toLowerCase() : '';
                                 })()}
                               </div>
                               <div className="text-blue-600 text-sm">
                                 {exp.organization || exp.company || 'Organization'}
                               </div>
                               <div className="text-xs text-slate-500 mt-1">
                                 {exp.location ? `${exp.location} • ` : ''}
                                 {(exp.start_year || exp.startYear) ? `${exp.start_year || exp.startYear} - ${exp.end_year || exp.endYear || 'Present'}` : ''}
                                 {(exp.years && exp.years > 0) ? ` • ${exp.years} years` : ''}
                                 {exp.duration ? ` • ${exp.duration}` : ''}
                               </div>
                             </div>
                           )}
                         </div>
                         {index < filteredArray.length - 1 && (
                           <hr className="border-slate-200" />
                         )}
                       </div>
                     )) : (
                       <div className="p-4">
                         <div className="text-slate-700">
                           {typeof (tutor.professionalExperience || tutor.experience) === 'string' ? 
                            (tutor.professionalExperience || tutor.experience) : 
                            'Professional experience available'}
                         </div>
                       </div>
                     )}
                  </div>
                </div>
              )}

              {/* Education */}
              {tutor.education && (
                <div className="mb-8">
                  <SectionHeader icon={GraduationCap} title="Education" />
                  <div className="space-y-4">
                    {Array.isArray(tutor.education) ? 
                     tutor.education.map((edu: any, index: number) => (
                       <div key={index} className="p-4 bg-white/50 backdrop-blur-sm rounded-lg border border-slate-200/50">
                         {typeof edu === 'string' ? (
                           <div className="text-slate-700">{edu}</div>
                         ) : (
                           <div>
                             <div className="font-medium text-slate-800">
                               {edu.degree || edu.level || 'Degree'}
                               {edu.field ? ` in ${edu.field}` : ''}
                             </div>
                             <div className="text-blue-600 text-sm">{edu.institution || 'Institution'}</div>
                             <div className="text-xs text-slate-500 mt-1">
                               {edu.country ? `${edu.country} • ` : ''}
                               {edu.year_of_completion ? `Graduated ${edu.year_of_completion}` : ''}
                               {(edu.gpa && edu.gpa_total && edu.gpa > 0) ? ` • GPA: ${edu.gpa}/${edu.gpa_total}` : ''}
                             </div>
                           </div>
                         )}
                       </div>
                     )) : (
                       <div className="p-4 bg-white/50 backdrop-blur-sm rounded-lg border border-slate-200/50">
                         <div className="text-slate-700">
                           {typeof tutor.education === 'string' ? 
                            tutor.education : 
                            (tutor.education.degree ? 
                             `${tutor.education.degree} from ${tutor.education.institution}` : 
                             'Educational background available')}
                         </div>
                       </div>
                     )}
                  </div>
                </div>
              )}


              {/* Featured Review */}
              {tutor.reviews && tutor.reviews.length > 0 && tutor.reviews[0] && 
               (tutor.reviews[0].comment || tutor.reviews[0].text || tutor.reviews[0].review) && (
                <div className="mb-8">
                  <SectionHeader icon={MessageCircle} title="Recent Review" />
                  <FeaturedReview review={tutor.reviews[0]} />
              </div>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="p-6 border-t border-slate-200/50 bg-white/50 backdrop-blur-sm">
          <div className="flex gap-3 w-full">
            <Button 
              variant="outline" 
              onClick={onClose}
              className={showBookingButton ? "flex-1 border-slate-300 hover:bg-slate-50" : "w-full border-slate-300 hover:bg-slate-50"}
            >
            Close
          </Button>
            {showBookingButton && (
          <Button
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                asChild
              >
                <Link to={`/book-session/${tutor.id}`}>
                  Book Session
                </Link>
          </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TutorProfileModal;